// IBKR chat: trading assistant (design: "Trading Assistant Chat"). Messages go to the
// backend agent (rflx-backend/ibkr_agent.py), which answers with text plus
// widgets; every widget polls its live data every 0.5s while visible
// (poller.ts). Order windows opened from widget buttons are added locally.

import { IDockviewPanelProps } from "dockview-react";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { agentChat, errorText } from "./api";
import { ChartCardWidget } from "./ChartCardWidget";
import { ChartView, openChartWindow } from "./ChartWindow";
import { LadderWidget } from "./LadderWidget";
import { LADDER_DURATIONS, useLadderSettings } from "./ladderSettings";
import { OptionsWidget } from "./OptionsWidget";
import { OrdersWidget } from "./OrdersWidget";
import { PositionsWidget } from "./PositionsWidget";
import { QuoteWidget } from "./QuoteWidget";
import { SolverWidget } from "./SolverWidget";
import { useIBKRChatStore } from "./store";
import { TicketWidget } from "./TicketWidget";
import { TradesWidget } from "./TradesWidget";
import { ChartParams, ChatMessage, Widget } from "./types";
import { C, FONT, StepChoice, stamp } from "./ui";
import { WidgetActions, WidgetProps } from "./windows";

const WIDGETS: Record<Widget["type"], (props: WidgetProps) => ReactElement> = {
  quote: QuoteWidget,
  options: OptionsWidget,
  positions: PositionsWidget,
  orders: OrdersWidget,
  trades: TradesWidget,
  ticket: TicketWidget,
  ladder: LadderWidget,
  chart: ChartCardWidget,
  solver: SolverWidget,
};

const FONT_HREF = "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&display=swap";

function useFont() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

// chats used to be persisted under this key
try {
  localStorage.removeItem("ibkr_fast_chats");
} catch {}

// command shortcuts, answered straight from IBKR without the LLM
// (rflx-backend/ibkr_commands.py); anything else goes to the assistant
// the command grammar, also shown next to the message input
const GRAMMAR = "<symbol> [<strike>c|p] [limit|ladder] [<size> [at <price>]]";
const CHAIN_GRAMMAR = "<symbol> c|p [<count>]";
const CHART_GRAMMAR = "<symbol> [<strike>c|p] [1d|7d] [<interval>] [rth] chart";

// the empty chat's help, one section per kind of command
const COMMAND_SECTIONS: { title: string; grammar: string; examples: [string, string][] }[] = [
  {
    title: "Stocks",
    grammar: "<symbol> [limit|ladder] [<size> [at <price>]]",
    examples: [
      ["iwm", "price of IWM"],
      ["iwm 10", "ladder, 10 shares"],
      ["iwm 10 at 222.2", "limit, 10 shares at 222.20"],
    ],
  },
  {
    title: "Options",
    grammar: "<symbol> <strike>c|p [limit|ladder] [<size> [at <price>]]",
    examples: [
      ["iwm 230c", "price of the IWM 230 call"],
      ["iwm 230c ladder", "ladder, size 1"],
      ["iwm 200c 10", "ladder, size 10"],
      ["iwm 230c limit", "limit, size 1, price from the quote"],
      ["iwm 230c 5 at 2.5", "limit, size 5 at 2.50"],
    ],
  },
  {
    title: "Option chains",
    grammar: CHAIN_GRAMMAR,
    examples: [
      ["iwm c", "3 calls above the current price"],
      ["iwm p 10", "10 puts below the current price"],
    ],
  },
  {
    title: "Charts",
    grammar: CHART_GRAMMAR,
    examples: [
      ["iwm chart", "the last 7 trading days in 1 min candles, outside RTH"],
      ["iwm 1d 5m chart", "today's session in 5 min candles"],
      ["iwm 230c rth chart", "the option, its underlying (regular hours) and IV"],
    ],
  },
];

function EmptyHint() {
  return (
    <div style={{ color: C.muted, display: "flex", flexDirection: "column", gap: 6 }}>
      <span>Quick commands, answered straight from IBKR (auto):</span>
      {COMMAND_SECTIONS.map((section) => (
        <div key={section.title} style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
          <span style={{ color: C.text, fontWeight: 600 }}>{section.title}</span>
          <span style={{ color: C.text }}>{section.grammar}</span>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {section.examples.map(([msg, what]) => (
              <li key={msg}><span style={{ color: C.text }}>{msg}</span> — {what}</li>
            ))}
          </ul>
        </div>
      ))}
      <span>
        Defaults: expiry Friday this week (Mon–Wed) or next week (Thu–Fri), size 1, limit price from the quote.
        A number is the size; {"\"at <price>\""} after it sets the price (limit order). Buy or sell is picked in the order window.
        Chart ranges: 7d (default, trading days) or 1d (today's session); intervals: 1s 5s 10s 15s 30s 1m 2m 3m 5m 10m 15m 20m 30m 1h 2h 3h 4h 8h (1m = 1 minute); the card's Open shows the live chart.
        Other ranges (e.g. "iwm from oct 1 to oct 15 hourly chart", "iwm last 300 candles chart", daily bars) go to the assistant.
      </span>
      <span>
        Anything else is answered by the assistant (llm), e.g. "jblu calls nov20", "positions", "trades today",
        "cancel my iwm order", option math like "what IV is 2.5 for the iwm 230c" and then "and if iwm = 240, iv = 0.3, price?".
      </span>
    </div>
  );
}

const blueButton = { height: 32, padding: "0 14px", background: C.buy, color: "#fff", border: 0, fontSize: 12.5, cursor: "pointer" };

// expanded by the Settings button: seconds per step of the ladder windows (each
// ladder window has its own step size)
function LadderSettingsPanel() {
  const { stepDuration, set } = useLadderSettings();
  const row = (label: string, control: ReactElement) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ width: 120, fontSize: 12.5 }}>{label}</span>
      {control}
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 10px", border: `1px solid ${C.border}` }}>
      <span style={{ color: C.muted }}>Ladder order</span>
      {row("Seconds per step", <StepChoice options={LADDER_DURATIONS} value={stepDuration} onChange={(v) => set({ stepDuration: v })} />)}
    </div>
  );
}

// Dockview panel: every window is its own chat, which ends when the window is
// removed (not on unmount: dockview remounts panels when they move)
export function IBKRChat(props: IDockviewPanelProps) {
  const chatId = props.api.id;
  useEffect(() => {
    const sub = props.containerApi.onDidRemovePanel((panel) => {
      if (panel.id === chatId) useIBKRChatStore.getState().dropChat(chatId);
    });
    return () => sub.dispose();
  }, [props.containerApi, chatId]);
  const openChart = (params: ChartParams, title: string) => openChartWindow(props.containerApi, params, title);
  // params.message (e.g. a ticker from the Real-time Chart's "IB Fast") is sent once on open
  const initialMessage = typeof props.params?.message === "string" ? props.params.message : undefined;
  return <IBKRChatView chatId={chatId} openChart={openChart} initialMessage={initialMessage} />;
}

// the chat itself, shared by the Dockview panel and the mobile page
// (mobile: 16px input so iOS doesn't zoom in on focus, tighter padding).
// Without openChart (mobile) a chart card's Open shows the chart over the chat.
export function IBKRChatView({ chatId, isMobile = false, openChart, initialMessage }: { chatId: string; isMobile?: boolean; openChart?: (params: ChartParams, title: string) => void; initialMessage?: string }) {
  useFont();
  const chat = useIBKRChatStore((s) => s.chats[chatId]);
  const store = useIBKRChatStore.getState;
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [overlayChart, setOverlayChart] = useState<ChartParams | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const ladder = useLadderSettings();
  const feedRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // ready to type as soon as the window opens (after dockview has placed the floating panel)
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    store().ensureChat(chatId);
  }, [chatId, store]);

  const messages = useMemo(() => chat?.messages ?? [], [chat]);
  const count = messages.length;
  // follow new messages / windows
  const stickToBottom = useRef(true);
  useEffect(() => {
    stickToBottom.current = true;
    const t = setTimeout(() => {
      const f = feedRef.current;
      if (f) f.scrollTop = f.scrollHeight;
    }, 50);
    return () => clearTimeout(t);
  }, [count, thinking]);
  // widgets grow once their data arrives: stay at the bottom while the user hasn't scrolled up
  useEffect(() => {
    const f = feedRef.current;
    if (!f) return;
    const onScroll = () => {
      stickToBottom.current = f.scrollHeight - f.scrollTop - f.clientHeight < 40;
    };
    const observer = new ResizeObserver(() => {
      if (stickToBottom.current) f.scrollTop = f.scrollHeight;
    });
    Array.from(f.children).forEach((c) => observer.observe(c));
    const mutations = new MutationObserver(() => Array.from(f.children).forEach((c) => observer.observe(c)));
    mutations.observe(f, { childList: true });
    f.addEventListener("scroll", onScroll);
    return () => {
      observer.disconnect();
      mutations.disconnect();
      f.removeEventListener("scroll", onScroll);
    };
  }, []);

  const send = async (message?: string) => {
    const text = (message ?? input).trim();
    if (!text || thinking || !chat) return;
    if (message === undefined) setInput("");
    // the chat before this message, as on screen
    const screen = chat.messages;
    store().addMessage(chatId, { role: "user", text });
    setThinking(true);
    try {
      const reply = await agentChat(chat.sessionId, text, screen);
      store().addMessage(chatId, { role: "assistant", text: reply.text, widgets: reply.widgets, source: reply.source });
    } catch (e) {
      store().addMessage(chatId, { role: "assistant", text: `Error: ${errorText(e)}` });
    } finally {
      setThinking(false);
    }
  };

  // send the initial message once, into a still-empty chat (dockview remounts panels
  // when they move, so an already-started chat doesn't resend it)
  const initialSent = useRef(false);
  useEffect(() => {
    if (!initialMessage || initialSent.current || !chat || chat.messages.length) return;
    initialSent.current = true;
    send(initialMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, chat]);

  const actionsFor = (widget: Widget): WidgetActions => ({
    open: (text, w, options) => store().openWindow(chatId, text, w, options),
    closeWhere: (match) => store().removeWidgets(chatId, match),
    patch: (params) => store().patchWidget(chatId, widget.id, params),
    duplicate: (params) => store().duplicateWidget(chatId, widget.id, params),
    close: () => store().removeWidget(chatId, widget.id),
    openChart: (params, title) => (openChart ? openChart(params, title) : setOverlayChart(params)),
  });

  // the lists render their intro themselves, with the live count
  const openList = (type: "orders" | "positions" | "trades") => store().openWindow(chatId, "", { type, params: {} }, { liveIntro: true });

  const renderMessage = (m: ChatMessage) => (
    <div key={m.id} style={{ flex: "none", display: "flex", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {!m.liveIntro && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ color: C.muted }}>{stamp(m.ts)} ({m.role === "user" ? "you" : m.source ?? "assistant"})</span>
            {m.text && <span style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{m.text}</span>}
          </div>
        )}
        {m.widgets.map((w) => {
          const Component = WIDGETS[w.type];
          return Component ? <Component key={w.id} widget={w} actions={actionsFor(w)} intro={m.liveIntro ? `${stamp(m.ts)} (assistant)` : undefined} /> : null;
        })}
      </div>
    </div>
  );

  return (
    <div style={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", background: "#fff", color: C.text, fontFamily: FONT, fontSize: 12, lineHeight: 1.5 }}>
      <div ref={feedRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: isMobile ? "12px 10px" : "16px 14px", display: "flex", flexDirection: "column", gap: 16 }}>
        {messages.length === 0 && <EmptyHint />}
        {messages.map(renderMessage)}
        {thinking && <span style={{ color: C.muted }}>{stamp(Date.now())} (assistant) …</span>}
      </div>

      <div style={{ flex: "none", borderTop: `1px solid ${C.border}`, padding: isMobile ? "8px 10px 10px" : "10px 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={blueButton} onClick={() => openList("orders")}>Orders</button>
          <button style={blueButton} onClick={() => openList("positions")}>Positions</button>
          <button style={blueButton} onClick={() => openList("trades")}>Transactions</button>
          <button style={{ ...blueButton, ...(settingsOpen ? { background: "#fff", color: C.text, border: `1px solid ${C.border}` } : {}) }}
            aria-expanded={settingsOpen} aria-label="Ladder settings" onClick={() => setSettingsOpen((o) => !o)}>
            {/* closed: the current seconds per ladder step */}
            {settingsOpen ? "Settings" : `t=${ladder.stepDuration}`}
          </button>
        </div>
        {settingsOpen && <LadderSettingsPanel />}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label htmlFor={`msg-${chatId}`} style={{ fontSize: 12.5, display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 10 }}>
            Message:
            <span style={{ color: "#9CA3AF", fontSize: 11.5 }}>{GRAMMAR}</span>
            <span style={{ color: "#9CA3AF", fontSize: 11.5 }}>{CHAIN_GRAMMAR}</span>
            <span style={{ color: "#9CA3AF", fontSize: 11.5 }}>{CHART_GRAMMAR}</span>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input ref={inputRef} id={`msg-${chatId}`} placeholder="New message" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              style={{ flex: 1, minWidth: 0, boxSizing: "border-box", height: 36, padding: "0 10px", border: `1px solid ${C.border}`, borderRadius: 0, background: "#fff", color: C.text, fontFamily: "inherit", fontSize: isMobile ? 16 : 13 }} />
            <button style={{ ...blueButton, height: 36, padding: "0 16px", opacity: thinking ? 0.6 : 1 }} disabled={thinking} onClick={() => send()}>Send</button>
          </div>
        </div>
      </div>
      {overlayChart && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "#fff" }}>
          <ChartView params={overlayChart} active onClose={() => setOverlayChart(null)} />
        </div>
      )}
    </div>
  );
}
